"use client";

/**
 * Terminal-style code window used across the API / MCP landing pages, plus
 * tiny token components (K/S/N/C/P) so examples stay readable in JSX:
 *
 *   <CodeWindow title="GET /api/v1/gex?symbol=SPY" status="200 - 38ms">
 *     <Line><K>&quot;symbol&quot;</K><P>: </P><S>&quot;SPY&quot;</S><P>,</P></Line>
 *   </CodeWindow>
 */

export function CodeWindow({
  title,
  method,
  status,
  children,
  className = "",
}: {
  title: string;
  method?: string;
  status?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-[#1f2020] bg-[#0d0e0e] text-left shadow-2xl shadow-black/50 ${className}`}
    >
      <div className="flex items-center gap-3 border-b border-[#1c1d1d] px-4 py-2.5">
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-[#2a2b2b]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#2a2b2b]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#2a2b2b]" />
        </span>
        {method ? (
          <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 font-mono text-[10px] tracking-wide text-emerald-300">
            {method}
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-[#8d918f]">{title}</span>
        {status ? (
          <span className="hidden shrink-0 items-center gap-1.5 font-mono text-[10px] text-[#8d918f] sm:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
            {status}
          </span>
        ) : null}
      </div>
      <pre className="overflow-x-auto p-4 font-mono text-[12.5px] leading-[1.7] text-[#d4d5d5] sm:p-5">
        {children}
      </pre>
    </div>
  );
}

/** One line of code. */
export function Line({ children, indent = 0 }: { children?: React.ReactNode; indent?: number }) {
  return (
    <div className="whitespace-pre">
      {indent > 0 ? " ".repeat(indent * 2) : null}
      {children}
    </div>
  );
}

/** JSON key / command - emerald glint. */
export function K({ children }: { children: React.ReactNode }) {
  return <span className="text-[#7fe7c8]">{children}</span>;
}

/** String value - polished silver. */
export function S({ children }: { children: React.ReactNode }) {
  return <span className="text-[#e8eae9]">{children}</span>;
}

/** Number value - warm white. */
export function N({ children }: { children: React.ReactNode }) {
  return <span className="text-[#f2f4f3]">{children}</span>;
}

/** Comment - dimmed. */
export function C({ children }: { children: React.ReactNode }) {
  return <span className="text-[#5b5b5b]">{children}</span>;
}

/** Punctuation / plain text - brushed steel. */
export function P({ children }: { children: React.ReactNode }) {
  return <span className="text-[#8d918f]">{children}</span>;
}
